import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C, getTradePalette } from '../../albumColors';
import { findTopMatches, buildBitmapFromCounts, bitmapAndToList } from '../../albumData';
import { albumService } from '../../../services/api';
import QuickSwapConfirmSheet from '../QuickSwapConfirmSheet';

/**
 * Smart-trade card. Hero (top match) keeps Quick-Swap + "Armar propuesta",
 * rows 2-N are condensed list entries with a profile icon and a "Ver match"
 * chevron so the user sees several partners instead of just one.
 */
const TOP_N = 5;

function SmartTradeCard({ myCounts, allUsers, tradeIndex, onViewUser, theme, onToast, myCommunities, lockedSet, incomingSet, onQuickSwap, softQuickSwap = false }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [previewUser, setPreviewUser] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const tradePalette = getTradePalette(theme);
  const notify = (message, variant = 'success') => {
    if (onToast) onToast(message, variant);
    else alert(message);
  };

  const communityNameById = useMemo(() => {
    const m = new Map();
    for (const c of myCommunities || []) m.set(c.id, c.name);
    return m;
  }, [myCommunities]);
  const communityLabel = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    const names = ids.map(id => communityNameById.get(id)).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length <= 2) return names.join(' · ');
    return `${names[0]} · +${names.length - 1}`;
  };

  const matches = useMemo(() => {
    if (!allUsers.length || !tradeIndex.size || !myCounts) return [];
    // Lock-aware spare: si tengo 2 pero 1 está reservada en otro trade, no es
    // realmente disponible para sumar a esta sugerencia. Sin esto el "te dan
    // / les das" sobreestimaba la cantidad real swappeable.
    // Incoming-aware needs: si voy a recibir una figurita en un trade ACCEPTED
    // pero todavía no completado, ya no la necesito — no la sugerimos como
    // "te dan" en futuros matches.
    const effectiveForSpare = (lockedSet && lockedSet.size > 0)
      ? myCounts.map((c, i) => Math.max(0, c - (lockedSet.get(i + 1) || 0)))
      : myCounts;
    const effectiveForNeeds = (incomingSet && incomingSet.size > 0)
      ? effectiveForSpare.map((c, i) => c + (incomingSet.get(i + 1)?.length || 0))
      : effectiveForSpare;
    const mySpare = buildBitmapFromCounts(effectiveForSpare, c => c >= 2);
    const myNeeds = buildBitmapFromCounts(effectiveForNeeds, c => c === 0);
    return findTopMatches(mySpare, myNeeds, allUsers, tradeIndex, TOP_N);
  }, [myCounts, allUsers, tradeIndex, lockedSet, incomingSet]);

  if (matches.length === 0) return null;

  const hero = matches[0];
  const rest = matches.slice(1);

  const cardBg     = theme?.smartTradeCardBg     ?? theme?.cardSurfaceStrong ?? C.surfaceContainerHigh;
  const cardBorder = theme?.smartTradeCardBorder ?? theme?.cardRowBorder     ?? `${C.outlineVariant}33`;
  const buttonBg   = theme?.smartTradeButtonBg   ?? theme?.primaryAccent     ?? C.primary;
  const buttonText = theme?.smartTradeButtonText ?? '#ffffff';

  const headingText = theme?.cardHeadingText ?? C.onSurfaceVariant;
  const bodyText    = theme?.cardBodyText    ?? C.onSurface;
  const subtleText  = theme?.cardSubtleText  ?? C.outline;
  const accentDim   = theme?.mutedText       ?? C.primaryDim;
  const accent      = theme?.primaryAccent   ?? C.primary;
  const receiveBg   = tradePalette.receive.bg ?? '#F59E0B';
  const receiveStrong = tradePalette.receive.strong ?? '#B45309';
  const giveBg      = tradePalette.give.bg ?? accent;
  const giveStrong  = tradePalette.give.strong ?? accentDim;
  const rowHover    = theme?.cardRowHoverBg  ?? C.surfaceContainerHigh;
  const chevron     = theme?.cardSubtleText  ?? C.outlineVariant;

  // Quick-swap se arma 100% en el browser: ya tenemos los bitmaps de ambos
  // lados (mySpare/myNeeds + their.spare/their.needs vía tradeIndex), así
  // que materializamos las dos listas (offered = mías que ellos necesitan,
  // requested = suyas que yo necesito) y las balanceamos al min de ambas.
  // El backend valida ownership cuando se envía el trade — no hace falta
  // round-trip extra para "preview".
  const handleQuickSwap = () => {
    const their = tradeIndex.get(hero.user.id);
    if (!their) {
      notify(t('No hay un intercambio rápido posible con {{name}}', { name: hero.user.name }), 'info');
      return;
    }
    const effectiveForSpare = (lockedSet && lockedSet.size > 0)
      ? myCounts.map((c, i) => Math.max(0, c - (lockedSet.get(i + 1) || 0)))
      : myCounts;
    const effectiveForNeeds = (incomingSet && incomingSet.size > 0)
      ? effectiveForSpare.map((c, i) => c + (incomingSet.get(i + 1)?.length || 0))
      : effectiveForSpare;
    const mySpare = buildBitmapFromCounts(effectiveForSpare, c => c >= 2);
    const myNeeds = buildBitmapFromCounts(effectiveForNeeds, c => c === 0);
    const offeredAll = bitmapAndToList(mySpare, their.needs);    // tengo, necesitan
    const requestedAll = bitmapAndToList(their.spare, myNeeds);   // tienen, necesito
    const swapSize = Math.min(offeredAll.length, requestedAll.length);
    if (swapSize === 0) {
      notify(t('No hay un intercambio rápido posible con {{name}}', { name: hero.user.name }), 'info');
      return;
    }
    const offered = offeredAll.slice(0, swapSize);
    const requested = requestedAll.slice(0, swapSize);
    if (onQuickSwap) {
      onQuickSwap(hero.user, offered, requested);
    } else {
      // Fallback legacy: mostrar el sheet con los mismos arrays.
      setPreview({ offered, requested });
      setPreviewUser(hero.user);
    }
  };

  const handleConfirmQuickSwap = async () => {
    if (!preview || preview.offered.length === 0 || !previewUser) return;
    try {
      await albumService.proposeTrade(previewUser.id, preview.offered, preview.requested);
      notify(t('Propuesta enviada a {{name}}', { name: previewUser?.name || '' }), 'success');
      setPreview(null);
      setPreviewUser(null);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message || t('No se pudo enviar la propuesta');
      notify(msg, 'error');
    }
  };

  return (
    <section className="mb-8">
      <h4 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: headingText }}>
        {t('Mejores coincidencias')}
      </h4>

      {/* Hero — #1 match: chrome card with the same hover affordance as community cards. */}
      <div className="bg-surface-container p-3 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group border border-transparent hover:border-primary-fixed/30">
        {(() => {
          const swapSize = Math.min(hero.theyCanGive, hero.iCanGive);
          const heroCommunity = communityLabel(hero.user.communityIds);
          return (
            <a
              href={`/users/${hero.user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 mb-3 -mx-1 px-1 py-1 rounded transition-colors hover:bg-black/5"
              style={{ color: 'inherit', textDecoration: 'none' }}
              title={t('Ver perfil')}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                style={{ backgroundColor: hero.user.color }}
              >
                {hero.user.initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-bold truncate" style={{ color: bodyText }}>{hero.user.name}</p>
                  {heroCommunity && (
                    <p className="text-[10px] truncate shrink-0" style={{ color: subtleText }}>{heroCommunity}</p>
                  )}
                </div>
                <div className="flex items-center gap-2.5 mt-0.5 text-[10px] font-bold">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: receiveBg }} />
                    <span style={{ color: receiveStrong }}>{t('te dan {{n}}', { n: swapSize })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: giveBg }} />
                    <span style={{ color: giveStrong }}>{t('les das {{n}}', { n: swapSize })}</span>
                  </div>
                </div>
              </div>
            </a>
          );
        })()}
        <button
          onClick={handleQuickSwap}
          disabled={loadingId === hero.user.id}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-60"
          style={softQuickSwap
            ? { backgroundColor: 'transparent', color: buttonBg, border: `1px solid ${buttonBg}55` }
            : { backgroundColor: buttonBg, color: buttonText }}
        >
          <span className="material-symbols-outlined text-sm">bolt</span>
          {loadingId === hero.user.id ? t('Calculando...') : t('Intercambio rápido')}
        </button>
        <button
          onClick={() => onViewUser(hero.user)}
          className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all"
          style={{ backgroundColor: 'transparent', color: buttonBg, border: `1px solid ${buttonBg}55` }}
        >
          <span className="material-symbols-outlined text-sm">tune</span>
          {t('Armar propuesta')}
        </button>
      </div>

      {/* Rows 2..N — condensed list, single "Ver match" affordance. */}
      {rest.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {rest.map((m, idx) => (
            <div
              key={m.user.id}
              className="flex items-center gap-2.5 p-2 rounded-lg transition-all"
              onMouseEnter={e => e.currentTarget.style.backgroundColor = rowHover}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span className="text-[10px] font-black w-4 text-center" style={{ color: chevron }}>
                {idx + 2}
              </span>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                style={{ backgroundColor: m.user.color }}
              >
                {m.user.initial}
              </div>
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => onViewUser(m.user)}
              >
                <p className="text-[11px] font-bold truncate" style={{ color: bodyText }}>{m.user.name}</p>
                {communityLabel(m.user.communityIds) && (
                  <p className="text-[10px] truncate" style={{ color: subtleText }}>{communityLabel(m.user.communityIds)}</p>
                )}
                {(() => {
                  const swapSize = Math.min(m.theyCanGive, m.iCanGive);
                  if (swapSize <= 0) return null;
                  return (
                    <div className="flex items-center gap-2 text-[9px]">
                      <span style={{ color: receiveStrong }}>
                        <span className="font-bold">{swapSize}</span> {t('te dan')}
                      </span>
                      <span style={{ color: giveStrong }}>
                        <span className="font-bold">{swapSize}</span> {t('les das')}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <a
                href={`/users/${m.user.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded transition-colors hover:bg-black/10"
                style={{ color: subtleText, textDecoration: 'none' }}
                title={t('Ver perfil')}
                onClick={e => e.stopPropagation()}
              >
                <span className="material-symbols-outlined text-sm">person</span>
              </a>
              <span
                className="material-symbols-outlined text-sm cursor-pointer"
                style={{ color: chevron }}
                onClick={() => onViewUser(m.user)}
              >chevron_right</span>
            </div>
          ))}
        </div>
      )}

      {preview && previewUser && (
        <QuickSwapConfirmSheet
          preview={preview}
          otherUser={previewUser}
          onConfirm={handleConfirmQuickSwap}
          onClose={() => { setPreview(null); setPreviewUser(null); }}
          theme={theme}
        />
      )}
    </section>
  );
}

export default SmartTradeCard;
